import { ImapBasicAuthentication, ImapFullAuthentication } from '@unipile/provider-imap';
import { type ProviderAuthenticationIntentResponse } from '@unipile/types';
import {
  Button,
  Input,
  useStartProviderAuthIntent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  useHostedAuthSession,
  LocationConfig,
  Spinner,
  FieldGroup,
  Field,
  FieldLabel,
  FieldLegend,
  FieldSet,
  FieldSeparator,
  CancelButton,
  ScopesConsentScreen,
  useScopesConsent,
} from '@unipile/ui';
import { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

type Step = 'login' | 'scopes_consent';
type ImapCredentials = ImapBasicAuthentication | ImapFullAuthentication;

export function LoginScreen({
  onSuccess,
  onError,
  onCancel,
}: {
  onSuccess: (data: ProviderAuthenticationIntentResponse) => void;
  onError: (error: unknown) => void;
  onCancel: () => void;
}) {
  const { data: session } = useHostedAuthSession();

  // Scopes consent management
  const { scopes, hasScopes, brandName, cancelConsent } = useScopesConsent({ provider: 'imap' });

  const [step, setStep] = useState<Step>('login');
  const [credentials, setCredentials] = useState<ImapCredentials | null>(null);

  const { isPending, mutate } = useStartProviderAuthIntent({
    mutationConfig: {
      onSuccess: (response) => {
        // Clear stored credentials on success
        setCredentials(null);
        setStep('login');
        onSuccess(response);
      },
      onError: (error) => {
        // On error, go back to login and switch to advanced if credentials were from basic form
        setStep('login');
        // If we have credentials stored, switch to advanced with prefilled values
        if (credentials) {
          if ('user' in credentials) {
            // Basic form credentials
            toggleAdvancedView(credentials.user, credentials.password);
          } else {
            // Advanced form credentials - already in advanced, just keep the values
            toggleAdvancedView(credentials.imap.user, credentials.imap.password);
          }
          setCredentials(null);
        }
        onError(error);
      },
    },
  });

  const { t } = useTranslation();
  const [advancedView, setAdvancedView] = useState(false);
  const [defaultUsername, setDefaultUsername] = useState(session?.config?.global?.login_hint || '');
  const [defaultPassword, setDefaultPassword] = useState('');

  const toggleAdvancedView = (username = '', password = '') => {
    setDefaultUsername(username);
    setDefaultPassword(password);
    setAdvancedView(true);
  };

  const handleCredentialsSubmit = (data: ImapCredentials) => {
    // Always store credentials before submission (for error handling)
    setCredentials(data);

    // If there are scopes, show consent screen first (DO NOT submit yet)
    if (hasScopes) {
      setStep('scopes_consent');
    } else {
      // No scopes, submit directly
      mutate({ data: { provider: 'imap', credentials: data } });
    }
  };

  // Show scopes consent screen after credentials (outside tabs)
  if (step === 'scopes_consent' && credentials) {
    return (
      <ScopesConsentScreen
        scopes={scopes}
        brandName={brandName}
        onAccept={() => {
          mutate({ data: { provider: 'imap', credentials } });
        }}
        onCancel={() => {
          cancelConsent();
          setStep('login');
          setCredentials(null);
        }}
        isPending={isPending}
      />
    );
  }

  return (
    <>
      <Tabs
        defaultValue="basic"
        value={advancedView ? 'advanced' : 'basic'}
        onValueChange={(value) => setAdvancedView(value === 'advanced')}
      >
        <TabsList className="w-full">
          <TabsTrigger className="w-full" value="basic">
            {t('LoginScreen.form.basic_configuration', { ns: 'imap' })}
          </TabsTrigger>
          <TabsTrigger className="w-full" value="advanced">
            {t('LoginScreen.form.advanced_configuration', { ns: 'imap' })}
          </TabsTrigger>
        </TabsList>

        <TabsContent value="basic">
          <BasicForm
            onCredentialsSubmit={handleCredentialsSubmit}
            username={defaultUsername}
            password={defaultPassword}
            isPending={isPending}
          />
        </TabsContent>
        <TabsContent value="advanced">
          <AdvancedForm
            onCredentialsSubmit={handleCredentialsSubmit}
            username={defaultUsername}
            password={defaultPassword}
            isPending={isPending}
          />
        </TabsContent>
      </Tabs>
      <CancelButton onCancel={onCancel} disabled={isPending} />
    </>
  );
}

function BasicForm({
  onCredentialsSubmit,
  username,
  password,
  isPending,
}: {
  onCredentialsSubmit: (data: ImapBasicAuthentication) => void;
  username: string;
  password: string;
  isPending: boolean;
}) {
  const form = useForm<ImapBasicAuthentication>({
    defaultValues: {
      user: username,
      password,
    },
  });

  const { t } = useTranslation();

  const onSubmit = async (data: ImapBasicAuthentication) => {
    // Do NOT submit here - just pass credentials to parent
    // Parent will handle consent screen and submission
    onCredentialsSubmit(data);
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <FieldGroup>
        <Controller
          control={form.control}
          name="user"
          rules={{ required: t('LoginScreen.form.username.required', { ns: 'imap' }) }}
          render={({ field }) => (
            <Field>
              <FieldLabel>{t('LoginScreen.form.username.label', { ns: 'imap' })}</FieldLabel>
              <Input type="email" placeholder={t('LoginScreen.form.username.label', { ns: 'imap' })} {...field} />
            </Field>
          )}
        />

        <Controller
          control={form.control}
          name="password"
          rules={{ required: t('LoginScreen.form.password.required', { ns: 'imap' }) }}
          render={({ field }) => (
            <Field>
              <FieldLabel>{t('LoginScreen.form.password.label', { ns: 'imap' })}</FieldLabel>
              <Input type="password" placeholder="••••••••" {...field} />
            </Field>
          )}
        />
        <LocationConfig provider="imap" />
        <Button type="submit" disabled={isPending}>
          {isPending && <Spinner />}
          {t('Common.next')}
        </Button>
      </FieldGroup>
    </form>
  );
}

function AdvancedForm({
  onCredentialsSubmit,
  username,
  password,
  isPending,
}: {
  onCredentialsSubmit: (data: ImapFullAuthentication) => void;
  username: string;
  password: string;
  isPending: boolean;
}) {
  const form = useForm<ImapFullAuthentication>({
    defaultValues: {
      imap: {
        host: '',
        port: 993,
        user: username,
        password,
        encryption: 'default',
      },
      smtp: {
        host: '',
        port: 465,
        user: username,
        password,
        encryption: 'default',
      },
    },
  });
  const { t } = useTranslation();

  const onSubmit = async (data: ImapFullAuthentication) => {
    onCredentialsSubmit(data);
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)}>
      <FieldGroup>
        <FieldSet>
          <FieldLegend>{t('LoginScreen.form.server.incoming_server.label', { ns: 'imap' })}</FieldLegend>

          <Controller
            control={form.control}
            name="imap.user"
            rules={{ required: t('LoginScreen.form.username.required', { ns: 'imap' }) }}
            render={({ field }) => (
              <Field>
                <FieldLabel>{t('LoginScreen.form.username.label', { ns: 'imap' })}</FieldLabel>
                <Input type="text" placeholder={t('LoginScreen.form.username.label', { ns: 'imap' })} {...field} />
              </Field>
            )}
          />

          <Controller
            control={form.control}
            name="imap.password"
            rules={{ required: t('LoginScreen.form.password.required', { ns: 'imap' }) }}
            render={({ field }) => (
              <Field>
                <FieldLabel>{t('LoginScreen.form.password.label', { ns: 'imap' })}</FieldLabel>
                <Input type="password" placeholder="••••••••" {...field} />
              </Field>
            )}
          />

          <div className="flex justify-between gap-2">
            <Controller
              control={form.control}
              name="imap.port"
              rules={{ required: t('LoginScreen.form.server.type.required', { ns: 'imap' }) }}
              render={() => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.type.label', { ns: 'imap' })}</FieldLabel>
                  <Input value="IMAP" disabled />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="imap.encryption"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.encryption.label', { ns: 'imap' })}</FieldLabel>

                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="DEFAULT" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tls">TLS</SelectItem>
                      <SelectItem value="ssl">SSL</SelectItem>
                      <SelectItem value="starttls">STARTTLS</SelectItem>
                      <SelectItem value="default">DEFAULT</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
            />
          </div>

          <div className="flex gap-2">
            <Controller
              control={form.control}
              name="imap.host"
              rules={{ required: t('LoginScreen.form.server.host.required', { ns: 'imap' }) }}
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.host.label', { ns: 'imap' })}</FieldLabel>
                  <Input placeholder={t('LoginScreen.form.server.host.label', { ns: 'imap' })} {...field} />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="imap.port"
              rules={{ required: t('LoginScreen.form.server.port.required', { ns: 'imap' }) }}
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.port.label', { ns: 'imap' })}</FieldLabel>
                  <Input
                    type="number"
                    placeholder={t('LoginScreen.form.server.port.label', { ns: 'imap' })}
                    {...field}
                    min={1}
                    max={65535}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? '' : Number(value));
                    }}
                  />
                </Field>
              )}
            />
          </div>
        </FieldSet>
        <FieldSeparator />
        <FieldSet>
          <FieldLegend>{t('LoginScreen.form.server.outgoing_server.label', { ns: 'imap' })}</FieldLegend>
          <Controller
            control={form.control}
            name="smtp.user"
            rules={{ required: t('LoginScreen.form.username.required', { ns: 'imap' }) }}
            render={({ field }) => (
              <Field>
                <FieldLabel>{t('LoginScreen.form.username.label', { ns: 'imap' })}</FieldLabel>
                <Input type="text" placeholder={t('LoginScreen.form.username.label', { ns: 'imap' })} {...field} />
              </Field>
            )}
          />
          <Controller
            control={form.control}
            name="smtp.password"
            rules={{ required: t('LoginScreen.form.password.required', { ns: 'imap' }) }}
            render={({ field }) => (
              <Field>
                <FieldLabel>{t('LoginScreen.form.password.label', { ns: 'imap' })}</FieldLabel>
                <Input type="password" placeholder="••••••••" {...field} />
              </Field>
            )}
          />
          <div className="flex justify-between gap-2">
            <Controller
              control={form.control}
              name="smtp.port"
              rules={{ required: t('LoginScreen.form.server.type.required', { ns: 'imap' }) }}
              render={() => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.type.label', { ns: 'imap' })}</FieldLabel>
                  <Input value="SMTP" disabled />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="smtp.encryption"
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.encryption.label', { ns: 'imap' })}</FieldLabel>
                  <Select onValueChange={field.onChange} defaultValue={field.value}>
                    <SelectTrigger>
                      <SelectValue placeholder="DEFAULT" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="tls">TLS</SelectItem>
                      <SelectItem value="ssl">SSL</SelectItem>
                      <SelectItem value="starttls">STARTTLS</SelectItem>
                      <SelectItem value="default">DEFAULT</SelectItem>
                    </SelectContent>
                  </Select>
                </Field>
              )}
            />
          </div>
          <div className="flex gap-2">
            <Controller
              control={form.control}
              name="smtp.host"
              rules={{ required: t('LoginScreen.form.server.host.required', { ns: 'imap' }) }}
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.host.label', { ns: 'imap' })}</FieldLabel>
                  <Input placeholder={t('LoginScreen.form.server.host.label', { ns: 'imap' })} {...field} />
                </Field>
              )}
            />

            <Controller
              control={form.control}
              name="smtp.port"
              rules={{ required: t('LoginScreen.form.server.port.required', { ns: 'imap' }) }}
              render={({ field }) => (
                <Field>
                  <FieldLabel>{t('LoginScreen.form.server.port.label', { ns: 'imap' })}</FieldLabel>
                  <Input
                    type="number"
                    placeholder={t('LoginScreen.form.server.port.label', { ns: 'imap' })}
                    {...field}
                    min={1}
                    max={65535}
                    onChange={(e) => {
                      const value = e.target.value;
                      field.onChange(value === '' ? '' : Number(value));
                    }}
                  />
                </Field>
              )}
            />
          </div>
        </FieldSet>
        <LocationConfig provider="imap" />
        <Button type="submit" disabled={isPending} className="w-full">
          {isPending && <Spinner />}
          {t('Common.next')}
        </Button>
      </FieldGroup>
    </form>
  );
}
